/**
 * TELV1-605 — embed URL query builder, ported verbatim from
 * feat/embed-v1-integration (TELV1-487). On merge of that branch, resolve this
 * file by taking theirs: their embedSnippets.ts is a superset.
 */

export interface EmbedQueryParam {
  key: string;
  value: string;
  /**
   * Percent-encode the value. Only for generator-controlled values (e.g.
   * `language`, whose labels carry spaces). Researcher-supplied values are
   * inserted raw — see buildEmbedQuery.
   */
  encodeValue?: boolean;
}

/**
 * TELV1-487 — assemble the embed URL query manually instead of via
 * URLSearchParams. Platform pipe tokens (`${e://Field/ResponseID}`,
 * `{IOM.Info.Serial}`, `[pipe: x]`, `^respid^`) must appear LITERALLY in the
 * pasted HTML for the host platform's piper to substitute them; form-encoding
 * them turns every respondent's ID into the literal token string. Researcher
 * values are inserted exactly as typed — the customer doc's URL-Encoding
 * section instructs pre-encoding literal values, and encoding again here
 * double-encoded them (`iPhone%2015` → `iPhone%252015`).
 *
 * Semantics preserved from the previous URLSearchParams implementation:
 * last-wins de-duplication per key (so a researcher-supplied `language`
 * still overrides the generated one) and skipping of empty keys/values.
 * Keys are encoded (they are plain identifiers, so this is a no-op guard);
 * only values ride raw.
 */
export const buildEmbedQuery = (params: EmbedQueryParam[]): string => {
  const merged = new Map<string, string>();
  params.forEach(({ key, value, encodeValue }) => {
    const k = key.trim();
    const v = value.trim();
    if (!k || !v) return;
    merged.set(encodeURIComponent(k), encodeValue ? encodeURIComponent(v) : v);
  });
  return Array.from(merged.entries())
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
};

/**
 * TELV1-605 — raw researcher values end up inside the iframe's `src="…"`
 * attribute. No platform pipe token contains `"`, `<` or `>`, so encoding
 * exactly those keeps every token literal while making it impossible for a
 * typed value to close the attribute or inject markup into the host page
 * (URLSearchParams used to encode them as a side effect).
 */
export const escapeForSrcAttribute = (value: string): string =>
  value.replace(/"/g, '%22').replace(/</g, '%3C').replace(/>/g, '%3E');
